import type { TaxScenario } from '@/tax/types';
import { formatCurrency, formatPercent, formatCompactCurrency } from '@/tax/format';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, MoreHorizontal, Edit3, Copy, Trash2, BarChart3, DollarSign, GitBranch, FolderOpen, FileEdit, Play, ArrowRight, CheckCircle2, GripVertical, Search, ChevronRight, ChevronDown, Eye, Download, FileText, FileSpreadsheet, Loader2, Pencil, Check, Presentation } from 'lucide-react';
import StrategyStoryDialog from '@/components/StrategyStoryDialog';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/safeSupabase';
import { toast } from 'sonner';
import { sanitizeError } from '@/lib/errorMessages';
import { generateComparisonPDF } from '@/lib/pdfExport';
import { generateComparisonExcel } from '@/lib/excelExport';
import type { ExportView } from '@/lib/exportView';

interface DashboardViewProps {
  scenarios: TaxScenario[];
  drafts: TaxScenario[];
  isAdmin?: boolean;
  canDeleteAll?: boolean;
  onEdit: (id: string) => void;
  onDuplicate: (scenario: TaxScenario) => void;
  onDelete: (id: string) => void;
  onDeleteMultiple: (ids: string[]) => void;
  onViewResults: (scenario: TaxScenario) => void;
  onNewScenario: () => void;
  onAddScenario: (scenario: TaxScenario) => void;
  onCompareGroup: (ids: string[]) => void;
  onResumeDraft: (draft: TaxScenario) => void;
  onDeleteDraft: (id: string) => void;
  onAcceptClient?: (clientId: string, groupName: string) => void;
  onDuplicateClient?: (clientId: string, clientName: string, baselineOnly: boolean) => void;
  onReorderPersist?: (scenarioIds: string[], sortOrders: number[]) => void;
  /** Open the focused single-client "Client View" page. When omitted, the button is hidden. */
  onClientView?: (clientId: string) => void;
  /**
   * Nickname a strategy scenario. Receives the scenario and the fully-formed new
   * name ("Client (nickname)") so the client-name prefix convention that
   * strategySuffix/exports parse is always preserved. When omitted, the rename
   * pencil is hidden.
   */
  onRenameScenario?: (scenario: TaxScenario, newName: string) => void;
  /** When set, render ONLY this client, force-expanded, and hide the list chrome (toolbar, drafts).
   *  This is the client-facing presentation view — no other client's name is rendered. */
  singleClientId?: string;
  /** Controlled accordion expand state, lifted so it survives navigation. When omitted, the
   *  component keeps its own collapsed-by-default state. */
  expandedClients?: Set<string>;
  onToggleExpand?: (clientId: string) => void;
  /** Org-scoped view: hide drafts, disable drag-reorder, show creator name next to each client. */
  isOrgView?: boolean;
  /** Creator id → display info, used to render "by {name}" on each client card when isOrgView is true. */
  creatorById?: Map<string, { full_name: string | null; email: string | null }>;
  // Transfer to Org (super admin only)
  orgsForTransfer?: { id: string; name: string; owner_id: string }[];
  onTransferComplete?: () => void;
}

interface ClientGroup {
  clientId: string;
  /** Lineage root — duplicated clients inherit this from their source. Original clients have rootClientId === clientId. */
  rootClientId: string;
  /** Baseline scenario's createdAt — used as the group's anchor for lineage sorting. */
  baselineCreatedAt: string;
  groupName: string;
  scenarios: TaxScenario[];
  drafts: TaxScenario[];
  /** user_id of whoever owns this client group (taken from the first scenario). Only used in org view. */
  creatorId?: string;
}

/**
 * Top-level sort order applied to whole lineages on the dashboard.
 * Within a lineage, members always go oldest-first (original on top, duplicates beneath)
 * regardless of which sort is selected — that's how the lineage-anchor design works.
 */
type SortMode = 'newest' | 'oldest' | 'recently-edited' | 'name-asc' | 'name-desc';

/**
 * Strips the client-name prefix from a scenario name and returns the strategy
 * suffix in parens. e.g. "TEst 2 (Roth + Alt)" → "Roth + Alt". Mirrors
 * ComparisonTable.tsx#strategySuffix. Falls back to the full name if no parens.
 */
function strategySuffix(name: string): string {
  const m = name.match(/\(([^)]+)\)\s*$/);
  return m ? m[1] : name;
}

function groupScenariosWithDrafts(
  scenarios: TaxScenario[],
  drafts: TaxScenario[],
  sortMode: SortMode = 'newest',
): { groups: ClientGroup[]; orphanDrafts: TaxScenario[] } {
  const map = new Map<string, { scenarios: TaxScenario[]; drafts: TaxScenario[] }>();
  const order: string[] = [];

  for (const s of scenarios) {
    const key = s.clientId;
    if (!map.has(key)) {
      map.set(key, { scenarios: [], drafts: [] });
      order.push(key);
    }
    map.get(key)!.scenarios.push(s);
  }

  const orphanDrafts: TaxScenario[] = [];
  for (const d of drafts) {
    const key = d.clientId;
    if (map.has(key)) {
      map.get(key)!.drafts.push(d);
    } else {
      orphanDrafts.push(d);
    }
  }

  const groups: ClientGroup[] = order.map(clientId => {
    const entry = map.get(clientId)!;
    const sorted = entry.scenarios.sort((a, b) =>
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.createdAt.localeCompare(b.createdAt)
    );
    return {
      clientId,
      rootClientId: sorted[0].rootClientId,
      baselineCreatedAt: sorted[0].createdAt,
      groupName: sorted[0].input.name,
      scenarios: sorted,
      drafts: entry.drafts,
      creatorId: sorted[0].userId,
    };
  });

  // Cluster groups by lineage so duplicates appear next to their original.
  // - Within a lineage: oldest first (original sits on top, duplicates follow in creation order).
  // - Across lineages: newest lineage on top, anchored by the root client's createdAt
  //   (or oldest member's createdAt if the root has been deleted). A duplicate of an
  //   old client inherits the old anchor, so it stays clustered with the original
  //   instead of jumping to the top of the dashboard.
  const byLineage = new Map<string, ClientGroup[]>();
  for (const g of groups) {
    const arr = byLineage.get(g.rootClientId) ?? [];
    arr.push(g);
    byLineage.set(g.rootClientId, arr);
  }

  const lineages = Array.from(byLineage.entries()).map(([rootId, members]) => {
    members.sort((a, b) => a.baselineCreatedAt.localeCompare(b.baselineCreatedAt));
    const root = members.find(g => g.clientId === rootId);
    const anchor = root?.baselineCreatedAt ?? members[0].baselineCreatedAt;
    // Lineage display name: root's name (or oldest member's if root was deleted) —
    // independent of which member is currently "newest." Keeps lineages stable
    // alphabetically even after duplicate "2", "3", etc.
    const lineageName = root?.groupName ?? members[0].groupName;
    // Most-recently-edited timestamp across every scenario in every member group
    // of the lineage. Used by the "Most recently edited" sort.
    let lastEdit = '';
    for (const g of members) {
      for (const s of g.scenarios) {
        if (s.updatedAt > lastEdit) lastEdit = s.updatedAt;
      }
      for (const d of g.drafts) {
        if (d.updatedAt > lastEdit) lastEdit = d.updatedAt;
      }
    }
    if (!lastEdit) lastEdit = anchor; // fallback if a lineage somehow has no rows
    return { anchor, lineageName, lastEdit, members };
  });

  switch (sortMode) {
    case 'oldest':
      lineages.sort((a, b) => a.anchor.localeCompare(b.anchor));
      break;
    case 'recently-edited':
      lineages.sort((a, b) => b.lastEdit.localeCompare(a.lastEdit));
      break;
    case 'name-asc':
      lineages.sort((a, b) => a.lineageName.localeCompare(b.lineageName, undefined, { sensitivity: 'base' }));
      break;
    case 'name-desc':
      lineages.sort((a, b) => b.lineageName.localeCompare(a.lineageName, undefined, { sensitivity: 'base' }));
      break;
    case 'newest':
    default:
      lineages.sort((a, b) => b.anchor.localeCompare(a.anchor));
  }

  return { groups: lineages.flatMap(l => l.members), orphanDrafts };
}

export default function DashboardView({
  scenarios, drafts, isAdmin, canDeleteAll = true,
  onEdit, onDuplicate, onDelete, onDeleteMultiple,
  onViewResults, onNewScenario, onAddScenario, onCompareGroup,
  onResumeDraft, onDeleteDraft, onAcceptClient, onDuplicateClient, onReorderPersist, orgsForTransfer, onTransferComplete,
  onClientView, onRenameScenario, singleClientId, expandedClients, onToggleExpand,
  isOrgView, creatorById,
}: DashboardViewProps) {
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  // Accordion expand state. Controlled by the parent when expandedClients/onToggleExpand are
  // provided (so it persists across navigation); otherwise kept locally, collapsed by default.
  const [internalExpanded, setInternalExpanded] = useState<Set<string>>(new Set());
  const expandedSet = expandedClients ?? internalExpanded;
  const toggleExpand = onToggleExpand ?? ((clientId: string) => {
    setInternalExpanded(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  });
  // Per-client-group custom order + selection for comparison.
  // Keyed by clientId. When a clientId isn't present, the default (createdAt-sorted) order
  // is used and every scenario is considered selected.
  const [groupState, setGroupState] = useState<Record<string, { selectedIds: string[]; order: string[] }>>({});
  const dragStateRef = useRef<{ clientId: string; scenarioId: string } | null>(null);
  const [deleteAllTarget, setDeleteAllTarget] = useState<{ clientId: string; groupName: string; ids: string[] } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteSingleTarget, setDeleteSingleTarget] = useState<{ id: string; name: string } | null>(null);
  const [dupTarget, setDupTarget] = useState<{ clientId: string; name: string; count: number } | null>(null);
  // Ref for auto-focusing the type-to-confirm input when the delete-all dialog opens.
  const deleteConfirmInputRef = useRef<HTMLInputElement>(null);
  const [transferTarget, setTransferTarget] = useState<{ clientId: string; groupName: string } | null>(null);
  const [transferOrgId, setTransferOrgId] = useState('');
  const [transferring, setTransferring] = useState(false);
  // clientId of the group currently generating an export — drives the per-card spinner.
  const [exportingClientId, setExportingClientId] = useState<string | null>(null);
  // Rename-pencil dialog: which strategy scenario is being nicknamed, plus the
  // group's baseline name (the prefix the nickname gets wrapped in) and the
  // nickname the dialog opened with (dirty-check for the dismiss guard).
  const [renameTarget, setRenameTarget] = useState<{ scenario: TaxScenario; baselineName: string; initialNickname: string } | null>(null);
  const [renameText, setRenameText] = useState('');
  // Bulk "Delete Selected" dialog: snapshot of the checked scenarios being deleted.
  const [deleteSelectedTarget, setDeleteSelectedTarget] = useState<{ groupName: string; scenarios: TaxScenario[] } | null>(null);
  // Whole-card selection (lineage view): pencil next to the heading toggles edit
  // mode (mirrors MyClientsGrid); checkboxes only render while it's on.
  const [groupEditMode, setGroupEditMode] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
  const [deleteVersionsTarget, setDeleteVersionsTarget] = useState<{ groups: ClientGroup[] } | null>(null);
  // Presentation mode: [baseline, ...checked strategies] handed to the story
  // dialog. Launched from the per-group Presentation icon button (flag-gated).
  const [presentScenarios, setPresentScenarios] = useState<TaxScenario[] | null>(null);
  const { isEnabled: isFlagEnabled } = useFeatureFlags();

  const openRename = (scenario: TaxScenario, baselineName: string) => {
    const initialNickname = strategySuffix(scenario.input.name);
    setRenameTarget({ scenario, baselineName, initialNickname });
    setRenameText(initialNickname);
  };

  // Parens are stripped from nicknames so the "Client (nickname)" convention
  // that strategySuffix parses (last paren group at end of name) stays intact.
  const cleanNickname = (raw: string) => raw.replace(/[()]/g, '').trim();

  const submitRename = () => {
    if (!renameTarget || !onRenameScenario) return;
    const nickname = cleanNickname(renameText);
    if (!nickname) return;
    onRenameScenario(renameTarget.scenario, `${renameTarget.baselineName} (${nickname})`);
    setRenameTarget(null);
  };

  // Dismiss guard: with an unsaved edit, Escape / click-outside / the X keep the
  // dialog open and warn — only Save or the explicit Cancel button close it.
  // Exists because a typed-but-unsaved nickname used to vanish silently.
  const renameDirty = !!renameTarget && cleanNickname(renameText) !== cleanNickname(renameTarget.initialNickname);

  const handleRenameOpenChange = (open: boolean) => {
    if (open) return;
    if (renameDirty) {
      toast.warning('Nickname not saved', { description: 'Click Save to keep it, or Cancel to discard.' });
      return;
    }
    setRenameTarget(null);
  };

  // Export the same ordered/selected scenarios the Compare button would open,
  // straight from the dashboard — no need to enter the comparison view first. The file
  // prints what the comparison page would show this person (Drew, 2026-10-05): the
  // same Cost Benefit flag the page renders under.
  const handleExportGroup = async (
    clientId: string,
    kind: 'pdf' | 'excel',
    idsToCompare: string[],
    scenariosById: Map<string, TaxScenario>,
  ) => {
    const toExport = idsToCompare
      .map(id => scenariosById.get(id))
      .filter((s): s is TaxScenario => !!s);
    if (toExport.length < 2) return;
    setExportingClientId(clientId);
    try {
      const view: ExportView = { journey: isFlagEnabled('cost_benefit_journey') };
      if (kind === 'pdf') await generateComparisonPDF(toExport, view);
      else await generateComparisonExcel(toExport, view);
    } catch (err) {
      toast.error('Export failed', { description: sanitizeError(err) });
    } finally {
      setExportingClientId(null);
    }
  };

  // Auto-focus the type-to-confirm input the moment the delete dialog opens, so the
  // user can start typing immediately instead of clicking into the field. The
  // setTimeout gives the dialog one tick to mount the input into the DOM.
  useEffect(() => {
    if (deleteAllTarget) {
      const t = setTimeout(() => deleteConfirmInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [deleteAllTarget]);

  /**
   * Reconcile stored group state against the current scenario IDs for a group.
   * - Drops any IDs that no longer exist.
   * - Appends any new IDs to the end (and marks them selected by default).
   * - Returns the default order + fully-selected state when the group has never been touched.
   */
  const getGroupState = (clientId: string, defaultOrder: string[]) => {
    const existing = groupState[clientId];
    if (!existing) {
      return { selectedIds: defaultOrder, order: defaultOrder };
    }
    const idSet = new Set(defaultOrder);
    const order = existing.order.filter(id => idSet.has(id));
    const knownSet = new Set(order);
    for (const id of defaultOrder) if (!knownSet.has(id)) order.push(id);
    const selectedIds = existing.selectedIds.filter(id => idSet.has(id));
    const existingSet = new Set(existing.order);
    for (const id of defaultOrder) if (!existingSet.has(id)) selectedIds.push(id);
    return { selectedIds, order };
  };

  // Both mutators below start from getGroupState (the reconciled view), NOT the raw
  // stored entry. Once a group has an entry, rows added later (a "Copy", a new
  // strategy) exist only in the reconciled order — the stored one predates them.
  // Reading the raw entry made a fresh copy undraggable: indexOf returned -1 and
  // the drop was silently ignored, with no error and no toast.
  const toggleGroupSelection = (clientId: string, scenarioId: string, defaultOrder: string[]) => {
    const current = getGroupState(clientId, defaultOrder);
    const selectedSet = new Set(current.selectedIds);
    if (selectedSet.has(scenarioId)) selectedSet.delete(scenarioId);
    else selectedSet.add(scenarioId);
    setGroupState(prev => ({ ...prev, [clientId]: { ...current, selectedIds: Array.from(selectedSet) } }));
  };

  const reorderGroup = (clientId: string, fromId: string, toId: string, defaultOrder: string[]) => {
    if (isOrgView) return;
    if (fromId === toId) return;
    // Baseline (first scenario) is locked — never move it, never drop before it
    if (fromId === defaultOrder[0] || toId === defaultOrder[0]) return;
    // Compute the new order outside the state updater — side effects inside
    // updaters are unreliable (React StrictMode double-invokes them).
    const current = getGroupState(clientId, defaultOrder);
    const nextOrder = [...current.order];
    const fromIdx = nextOrder.indexOf(fromId);
    const toIdx = nextOrder.indexOf(toId);
    if (fromIdx === -1 || toIdx === -1) return;
    nextOrder.splice(fromIdx, 1);
    nextOrder.splice(toIdx, 0, fromId);
    setGroupState(prev => ({ ...prev, [clientId]: { ...current, order: nextOrder } }));
    onReorderPersist?.(nextOrder, nextOrder.map((_, i) => i));
  };

  const handleTransfer = async () => {
    if (!transferTarget || !transferOrgId) return;
    setTransferring(true);
    try {
      const targetOrg = orgsForTransfer?.find(o => o.id === transferOrgId);
      if (!targetOrg) throw new Error('Org not found');

      // Update all scenarios for this client
      const { error } = await supabase
        .from('scenarios')
        .update({ org_id: transferOrgId, user_id: targetOrg.owner_id } as any)
        .eq('client_id', transferTarget.clientId);
      if (error) throw error;

      // Delete any assignments for this client
      await supabase.from('case_assignments').delete().eq('client_id', transferTarget.clientId);

      // Log audit
      await supabase.rpc('log_audit', {
        p_action: 'client.transferred',
        p_org_id: transferOrgId,
        p_target_type: 'client',
        p_target_id: transferTarget.clientId as any,
        p_details: { client_name: transferTarget.groupName, target_org: targetOrg.name },
      });

      toast.success('Transferred', { description: `${transferTarget.groupName} transferred to ${targetOrg.name}` });
      setTransferTarget(null);
      setTransferOrgId('');
      onTransferComplete?.();
    } catch (err: any) {
      toast.error('Error', { description: sanitizeError(err) });
    } finally {
      setTransferring(false);
    }
  };

  if (scenarios.length === 0 && drafts.length === 0) {
    return (
      <Card>
        <CardContent className="py-20 text-center text-muted-foreground">
          <DollarSign className="h-14 w-14 mx-auto mb-4 text-muted-foreground/20" />
          <p className="text-lg font-semibold mb-2">No clients yet</p>
          <p className="text-sm mb-6">Create your first client to get started.</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button onClick={onNewScenario} size="lg">
              <Plus className="h-4 w-4 mr-2" /> Create Client
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { groups: allGroups, orphanDrafts } = groupScenariosWithDrafts(scenarios, drafts, sortMode);

  const groups = search
    ? allGroups.filter(g => {
        const q = search.toLowerCase();
        if (g.groupName.toLowerCase().includes(q)) return true;
        if (g.scenarios.some(s => s.input.name.toLowerCase().includes(q))) return true;
        if (g.drafts.some(d => d.input.name.toLowerCase().includes(q))) return true;
        return false;
      })
    : allGroups;

  // Client View: render only the focused client, nothing else.
  const displayGroups = singleClientId ? groups.filter(g => g.clientId === singleClientId) : groups;
  const searchActive = search.trim().length > 0;

  // Whole-card selection for bulk-deleting client versions (the lineage view,
  // where each card is one version). Only offered when there's actually more
  // than one card and the user is allowed to delete whole clients, and only
  // while the pencil-toggled edit mode is on.
  const canEditGroups = canDeleteAll && !singleClientId && displayGroups.length > 1;
  const groupsSelectable = canEditGroups && groupEditMode;
  const selectedGroups = groupsSelectable
    ? displayGroups.filter(g => selectedClientIds.has(g.clientId))
    : [];
  const toggleClientSelected = (clientId: string) => {
    setSelectedClientIds(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const ScenarioRow = ({
    s,
    isFirst,
    isSelected,
    onToggleSelect,
    onRename,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
  }: {
    s: TaxScenario;
    isFirst: boolean;
    isSelected: boolean;
    onToggleSelect: () => void;
    /** Opens the nickname dialog. Only passed for strategy rows — the baseline's name IS the client name. */
    onRename?: () => void;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onDragEnd: () => void;
  }) => (
    <TableRow
      className="group"
      data-tour="strategy-row"
      draggable={!isFirst && !isOrgView}
      onDragStart={isFirst || isOrgView ? undefined : onDragStart}
      onDragOver={isFirst || isOrgView ? undefined : onDragOver}
      onDrop={isFirst || isOrgView ? undefined : onDrop}
      onDragEnd={isFirst || isOrgView ? undefined : onDragEnd}
    >
      <TableCell className="w-16 pr-0">
        <div className="flex items-center gap-1.5">
          {!isFirst && !isOrgView ? (
            <span
              className="cursor-grab text-muted-foreground/60 hover:text-foreground active:cursor-grabbing"
              aria-label="Drag to reorder"
              data-tour="strategy-drag"
            >
              <GripVertical className="h-4 w-4" />
            </span>
          ) : (
            <span className="w-4" />
          )}
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            aria-label={`Select ${s.input.name} for comparison`}
            data-tour="strategy-select"
          />
        </div>
      </TableCell>
      <TableCell className="font-medium">
        <div className="flex items-center gap-1">
          <button className="hover:text-primary transition-colors text-left flex items-center gap-1.5" onClick={() => onViewResults(s)}>
            {!isFirst && <GitBranch className="h-3 w-3 text-muted-foreground shrink-0" />}
            {s.input.name}
          </button>
          {onRename && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
              onClick={onRename}
              aria-label={`Rename ${s.input.name}`}
              title="Nickname this strategy"
              data-tour="strategy-rename"
            >
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell className="font-mono text-sm hidden sm:table-cell">{formatCompactCurrency(s.input.rothConversionAmount)}</TableCell>
      <TableCell className="font-mono text-sm hidden sm:table-cell">{formatCompactCurrency(s.input.alternativeInvestmentAmount)}</TableCell>
      <TableCell className="font-mono text-sm hidden sm:table-cell">{formatCompactCurrency(s.input.longTermCapitalGains)}</TableCell>
      <TableCell className="font-mono text-sm hidden md:table-cell">{formatCurrency(s.results.adjustedGrossIncome)}</TableCell>
      <TableCell className="font-mono text-sm font-semibold text-primary">{formatCurrency(s.results.totalTaxLiability)}</TableCell>
      <TableCell className="font-mono text-sm font-semibold text-secondary hidden sm:table-cell">{formatPercent(s.results.effectiveTaxRate)}</TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" data-tour="strategy-menu">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onViewResults(s)}>
              <BarChart3 className="h-3.5 w-3.5 mr-2" /> View Results
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onEdit(s.id)}>
              <Edit3 className="h-3.5 w-3.5 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDuplicate(s)}>
              <Copy className="h-3.5 w-3.5 mr-2" /> Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAddScenario(s)}>
              <GitBranch className="h-3.5 w-3.5 mr-2" /> Add Strategy
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setDeleteSingleTarget({ id: s.id, name: s.input.name })}
              className="text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );

  const DraftRow = ({ d }: { d: TaxScenario }) => (
    <TableRow className="group bg-muted/30">
      <TableCell className="w-16 pr-0" />
      <TableCell className="font-medium">
        <div className="flex items-center gap-1.5">
          <GitBranch className="h-3 w-3 text-muted-foreground shrink-0" />
          <span className="text-muted-foreground">{d.input.name || 'Untitled Draft'}</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-amber-500/50 text-amber-600">Draft</Badge>
        </div>
      </TableCell>
      <TableCell className="font-mono text-sm text-muted-foreground hidden sm:table-cell">{formatCompactCurrency(d.input.rothConversionAmount)}</TableCell>
      <TableCell className="font-mono text-sm text-muted-foreground hidden sm:table-cell">{formatCompactCurrency(d.input.alternativeInvestmentAmount)}</TableCell>
      <TableCell className="font-mono text-sm text-muted-foreground hidden sm:table-cell">{formatCompactCurrency(d.input.longTermCapitalGains)}</TableCell>
      <TableCell colSpan={3} className="text-xs text-muted-foreground italic hidden sm:table-cell">In progress…</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onResumeDraft(d)}>
            <Play className="h-3 w-3 mr-1" /> Resume
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => onDeleteDraft(d.id)}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  const renderGroupCard = (group: ClientGroup, isAssigned?: boolean) => {
    const hasDrafts = group.drafts.length > 0;
    const totalCount = group.scenarios.length + group.drafts.length;
    const isMulti = totalCount > 1;
    const allIds = [...group.scenarios.map(s => s.id), ...group.drafts.map(d => d.id)];

    const defaultOrder = group.scenarios.map(s => s.id);
    const { selectedIds, order } = getGroupState(group.clientId, defaultOrder);
    const selectedSet = new Set(selectedIds);
    const scenariosById = new Map(group.scenarios.map(s => [s.id, s] as const));
    const orderedScenarios = order
      .map(id => scenariosById.get(id))
      .filter((s): s is TaxScenario => !!s);
    const idsToCompare = order.filter(id => selectedSet.has(id));
    const canCompare = idsToCompare.length >= 2;
    // Collapsed by default. Forced open in the focused Client View, and auto-opened while searching
    // so matches aren't hidden behind a collapsed header.
    const isExpanded = !!singleClientId || searchActive || expandedSet.has(group.clientId);

    return (
      <Card key={group.clientId} className={isMulti ? 'border-primary/20 shadow-sm' : ''} data-tour="client-group">
        <CardHeader className={`pt-3 px-4 ${isExpanded ? 'pb-0' : 'pb-3'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {groupsSelectable && (
                <Checkbox
                  checked={selectedClientIds.has(group.clientId)}
                  onCheckedChange={() => toggleClientSelected(group.clientId)}
                  aria-label={`Select ${group.groupName} for deletion`}
                />
              )}
              {singleClientId ? (
                <span className="flex items-center gap-2 min-w-0">
                  <FolderOpen className="h-4 w-4 text-primary shrink-0" />
                  <span className="font-semibold text-sm truncate">{group.groupName}</span>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => toggleExpand(group.clientId)}
                  className="flex items-center gap-1.5 min-w-0 hover:text-primary transition-colors"
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${group.groupName}`}
                >
                  {isExpanded
                    ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                    : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                  <FolderOpen className="h-4 w-4 text-primary shrink-0" />
                  <span className="font-semibold text-sm truncate">{group.groupName}</span>
                  {!isExpanded && (
                    <span className="text-xs text-muted-foreground shrink-0">
                      ({totalCount} scenario{totalCount !== 1 ? 's' : ''})
                    </span>
                  )}
                </button>
              )}
              {isOrgView && group.creatorId && (() => {
                const creator = creatorById?.get(group.creatorId);
                const label = creator?.full_name || creator?.email || 'Unknown';
                return <span className="text-xs text-muted-foreground">by {label}</span>;
              })()}
              {isMulti && hasDrafts && !isOrgView && (
                <span className="text-xs text-muted-foreground">
                  ({group.drafts.length} draft{group.drafts.length !== 1 ? 's' : ''})
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {/* Client View per-row button intentionally removed: the lineage row-list
                  IS the client view (every variant is visible right here). Single-version
                  lineages still open straight to the focused Client View page via the
                  folder click on the grid. */}
              {isFlagEnabled('strategy_story') && group.scenarios.length > 1 && (() => {
                // Presentation icon (no label): opens presentation mode with the
                // CHECKED strategies. Baseline is always the anchor; one checked
                // strategy = single streamline, several = side by side.
                const baselineScenario = orderedScenarios[0];
                const presentIds = idsToCompare.filter(id => id !== baselineScenario?.id);
                const canPresent = !!baselineScenario && presentIds.length >= 1;
                return (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2"
                    disabled={!canPresent}
                    aria-label="Present"
                    data-tour="client-present"
                    title={canPresent ? 'Present the checked strategies' : 'Check at least one strategy to present'}
                    onClick={() => {
                      const chosen = presentIds
                        .map(id => scenariosById.get(id))
                        .filter((s): s is TaxScenario => !!s);
                      setPresentScenarios([baselineScenario, ...chosen]);
                    }}
                  >
                    <Presentation className="h-3.5 w-3.5" />
                  </Button>
                );
              })()}
              {group.scenarios.length > 1 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  disabled={!canCompare}
                  data-tour="client-compare"
                  onClick={() => onCompareGroup(idsToCompare)}
                  title={canCompare ? undefined : 'Select at least 2 scenarios to compare'}
                >
                  <BarChart3 className="h-3 w-3 mr-1" /> Compare ({idsToCompare.length})
                </Button>
              )}
              {group.scenarios.length > 1 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={!canCompare || exportingClientId === group.clientId}
                      data-tour="client-export"
                      title={canCompare ? undefined : 'Select at least 2 scenarios to export a comparison'}
                    >
                      {exportingClientId === group.clientId
                        ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        : <Download className="h-3 w-3 mr-1" />}
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleExportGroup(group.clientId, 'pdf', idsToCompare, scenariosById)}>
                      <FileText className="h-3.5 w-3.5 mr-2" /> PDF Report
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportGroup(group.clientId, 'excel', idsToCompare, scenariosById)}>
                      <FileSpreadsheet className="h-3.5 w-3.5 mr-2" /> Excel Workbook
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAddScenario(group.scenarios[0])} data-tour="client-add-scenario">
                <Plus className="h-3 w-3 mr-1" /> Add Strategy
              </Button>
              {onDuplicateClient && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => setDupTarget({ clientId: group.clientId, name: group.groupName, count: group.scenarios.length })}
                  title="Duplicate this client into a new version"
                  data-tour="client-duplicate"
                >
                  <Copy className="h-3 w-3 mr-1" /> Duplicate
                </Button>
              )}
              {/* Three-dot overflow menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" aria-label={`More actions for ${group.groupName}`} data-tour="client-more">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isAssigned && onAcceptClient && (
                    <DropdownMenuItem onClick={() => onAcceptClient(group.clientId, group.groupName)}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-2 text-green-600" /> Accept Client
                    </DropdownMenuItem>
                  )}
                  {isAssigned && onAcceptClient && (
                    <DropdownMenuSeparator />
                  )}
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    disabled={idsToCompare.length === 0}
                    onClick={() => {
                      const picked = idsToCompare
                        .map(id => scenariosById.get(id))
                        .filter((sc): sc is TaxScenario => !!sc);
                      if (picked.length === 0) return;
                      setDeleteSelectedTarget({ groupName: group.groupName, scenarios: picked });
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete Selected ({idsToCompare.length})
                  </DropdownMenuItem>
                  {canDeleteAll && (
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => {
                        setDeleteAllTarget({ clientId: group.clientId, groupName: group.groupName, ids: allIds });
                        setDeleteConfirmText('');
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete All Strategies
                    </DropdownMenuItem>
                  )}
                  {isAdmin && orgsForTransfer && orgsForTransfer.length > 0 && (
                    <DropdownMenuItem onClick={() => { setTransferTarget({ clientId: group.clientId, groupName: group.groupName }); setTransferOrgId(''); }}>
                      <ArrowRight className="h-3.5 w-3.5 mr-2" /> Transfer to Org
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        {isExpanded && (
        <CardContent className="p-2 pt-2">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16" />
                  <TableHead className="text-xs">Name</TableHead>
                  <TableHead className="text-xs hidden sm:table-cell">Roth</TableHead>
                  <TableHead className="text-xs hidden sm:table-cell">Alt</TableHead>
                  <TableHead className="text-xs hidden sm:table-cell">LTCG</TableHead>
                  <TableHead className="text-xs hidden md:table-cell">AGI</TableHead>
                  <TableHead className="text-xs">Total Tax</TableHead>
                  <TableHead className="text-xs hidden sm:table-cell">Eff. Rate</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderedScenarios.map((s, i) => (
                  <ScenarioRow
                    key={s.id}
                    s={s}
                    isFirst={i === 0}
                    isSelected={selectedSet.has(s.id)}
                    onToggleSelect={() => toggleGroupSelection(group.clientId, s.id, defaultOrder)}
                    onRename={i !== 0 && onRenameScenario ? () => openRename(s, group.groupName) : undefined}
                    onDragStart={(e) => {
                      dragStateRef.current = { clientId: group.clientId, scenarioId: s.id };
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => {
                      if (dragStateRef.current?.clientId === group.clientId) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragStateRef.current;
                      if (from && from.clientId === group.clientId) {
                        reorderGroup(group.clientId, from.scenarioId, s.id, defaultOrder);
                      }
                      dragStateRef.current = null;
                    }}
                    onDragEnd={() => { dragStateRef.current = null; }}
                  />
                ))}
                {!isOrgView && group.drafts.map(d => <DraftRow key={d.id} d={d} />)}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      {presentScenarios && (
        <StrategyStoryDialog
          scenarios={presentScenarios}
          open
          onOpenChange={(o) => { if (!o) setPresentScenarios(null); }}
        />
      )}
      {!singleClientId && (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 shrink-0">
          <CardTitle className="text-xl">
            {isAdmin ? 'Demo Data' : 'My Clients'} ({groups.length})
          </CardTitle>
          {canEditGroups && (
            groupEditMode ? (
              <Button
                size="sm"
                variant="default"
                className="h-7 px-2.5 text-xs gap-1.5"
                onClick={() => { setGroupEditMode(false); setSelectedClientIds(new Set()); }}
                title="Exit edit mode"
              >
                <Check className="h-3.5 w-3.5" /> Done
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                onClick={() => setGroupEditMode(true)}
                title="Edit clients — select cards to delete in bulk"
                aria-label="Edit clients"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )
          )}
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-1 sm:justify-end">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search clients…" className="pl-9 h-9" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <Select value={sortMode} onValueChange={(v) => setSortMode(v as SortMode)}>
            <SelectTrigger className="h-9 w-[180px] shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="recently-edited">Most recently edited</SelectItem>
              <SelectItem value="name-asc">Name A → Z</SelectItem>
              <SelectItem value="name-desc">Name Z → A</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={onNewScenario} size="sm" className="shrink-0">
            <Plus className="h-4 w-4 mr-1" /> New Client
          </Button>
        </div>
      </div>
      )}


      {/* Orphan drafts */}
      {!singleClientId && orphanDrafts.length > 0 && (
        <Card className="border-dashed border-muted-foreground/30">
          <CardHeader className="pb-2 pt-3 px-4">
            <div className="flex items-center gap-2">
              <FileEdit className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold text-sm">Drafts</span>
              <span className="text-xs text-muted-foreground">({orphanDrafts.length})</span>
            </div>
          </CardHeader>
          <CardContent className="p-2 pt-0">
            <div className="space-y-1">
              {orphanDrafts.map(d => (
                <div key={d.id} className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <FileEdit className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm font-medium">{d.input.name || 'Untitled Draft'}</span>
                    <span className="text-xs text-muted-foreground">
                      Roth {formatCompactCurrency(d.input.rothConversionAmount)} · Alt {formatCompactCurrency(d.input.alternativeInvestmentAmount)} · LTCG {formatCompactCurrency(d.input.longTermCapitalGains)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onResumeDraft(d)}>
                      <Play className="h-3 w-3 mr-1" /> Resume
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => onDeleteDraft(d.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk-delete action bar — appears once at least one version card is ticked. */}
      {selectedGroups.length > 0 && (
        <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
          <span className="text-sm font-medium">
            {selectedGroups.length} selected
          </span>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="destructive"
            className="h-8 text-xs gap-1.5"
            onClick={() => {
              setDeleteVersionsTarget({ groups: selectedGroups });
              setDeleteConfirmText('');
            }}
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete ({selectedGroups.length})
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-xs"
            onClick={() => setSelectedClientIds(new Set())}
          >
            Clear
          </Button>
        </div>
      )}

      {/* Client groups — flat list for all users (single client when in Client View) */}
      {displayGroups.map(g => renderGroupCard(g))}

      {singleClientId && displayGroups.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <p className="text-sm">This client is no longer available.</p>
          </CardContent>
        </Card>
      )}

      {/* Duplicate choice — what should the new version start with? */}
      <Dialog open={!!dupTarget} onOpenChange={open => { if (!open) setDupTarget(null); }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Duplicate {dupTarget?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">What should the new version start with?</p>
          <div className="space-y-2 py-1">
            <button
              className="w-full text-left rounded-md border border-border bg-card px-4 py-3 hover:bg-accent/50 hover:border-primary/40 transition-colors"
              onClick={() => {
                if (dupTarget && onDuplicateClient) onDuplicateClient(dupTarget.clientId, dupTarget.name, true);
                setDupTarget(null);
              }}
            >
              <span className="block text-sm font-semibold text-foreground">Baseline only</span>
              <span className="block text-xs text-muted-foreground mt-0.5">Just the base case. A clean slate to build new strategies on.</span>
            </button>
            <button
              className="w-full text-left rounded-md border border-border bg-card px-4 py-3 hover:bg-accent/50 hover:border-primary/40 transition-colors"
              onClick={() => {
                if (dupTarget && onDuplicateClient) onDuplicateClient(dupTarget.clientId, dupTarget.name, false);
                setDupTarget(null);
              }}
            >
              <span className="block text-sm font-semibold text-foreground">
                Everything ({dupTarget?.count} scenario{dupTarget?.count === 1 ? '' : 's'})
              </span>
              <span className="block text-xs text-muted-foreground mt-0.5">The base case plus every strategy, copied as-is.</span>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupTarget(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Single Scenario Confirmation */}
      <AlertDialog open={!!deleteSingleTarget} onOpenChange={open => { if (!open) setDeleteSingleTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete scenario?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold text-foreground">{deleteSingleTarget?.name}</span>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteSingleTarget) onDelete(deleteSingleTarget.id);
                setDeleteSingleTarget(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Selected Confirmation — one click for the whole batch. No typed
          gate: the dialog lists exactly what's going, and the 5-second undo toast
          (handleDeleteMultiple) is the safety net for fat-fingers. */}
      <AlertDialog open={!!deleteSelectedTarget} onOpenChange={open => { if (!open) setDeleteSelectedTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteSelectedTarget?.scenarios.length} selected scenario{(deleteSelectedTarget?.scenarios.length ?? 0) === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              These scenarios will be deleted from <span className="font-semibold text-foreground">{deleteSelectedTarget?.groupName}</span>. You can undo for a few seconds after deleting.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteSelectedTarget && (
            <div className="space-y-1 max-h-48 overflow-y-auto rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              {deleteSelectedTarget.scenarios.map(sc => (
                <div key={sc.id} className="truncate py-0.5 font-medium">{sc.input.name}</div>
              ))}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteSelectedTarget) onDeleteMultiple(deleteSelectedTarget.scenarios.map(sc => sc.id));
                setDeleteSelectedTarget(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete {deleteSelectedTarget?.scenarios.length}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Nickname (rename) Dialog — edits only the strategy suffix in parens;
          the client-name prefix is locked so comparisons/exports keep parsing.
          Dismissal is guarded while the nickname has unsaved edits. */}
      <Dialog open={!!renameTarget} onOpenChange={handleRenameOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nickname strategy</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="strategy-nickname">Nickname</Label>
            <Input
              id="strategy-nickname"
              autoFocus
              placeholder="e.g. Aggressive Roth"
              value={renameText}
              onChange={e => setRenameText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitRename();
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              Will appear as <span className="font-medium text-foreground">{renameTarget?.baselineName} ({cleanNickname(renameText) || '…'})</span> on the dashboard, in comparisons, and on exports.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button onClick={submitRename} disabled={!cleanNickname(renameText)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk-delete versions confirmation — one typed "delete" for the whole batch.
          Lists every version being removed with its scenario count; deletion fans
          out to onDeleteMultiple (optimistic + 5-second undo). */}
      <AlertDialog open={!!deleteVersionsTarget} onOpenChange={open => { if (!open) { setDeleteVersionsTarget(null); setDeleteConfirmText(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteVersionsTarget?.groups.length} client{(deleteVersionsTarget?.groups.length ?? 0) === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes every scenario in the selected clients. Type
              <span className="font-semibold text-foreground"> delete </span>
              to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteVersionsTarget && (
            <div className="space-y-1 max-h-48 overflow-y-auto rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              {deleteVersionsTarget.groups.map(g => {
                const total = g.scenarios.length + g.drafts.length;
                return (
                  <div key={g.clientId} className="flex items-center justify-between gap-3 py-0.5">
                    <span className="truncate font-medium">{g.groupName}</span>
                    <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                      {total} scenario{total === 1 ? '' : 's'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="py-2">
            <Input
              autoFocus
              placeholder="Type delete to confirm…"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && deleteConfirmText.trim().toLowerCase() === 'delete' && deleteVersionsTarget) {
                  e.preventDefault();
                  onDeleteMultiple(deleteVersionsTarget.groups.flatMap(g => [...g.scenarios.map(s => s.id), ...g.drafts.map(d => d.id)]));
                  setDeleteVersionsTarget(null);
                  setDeleteConfirmText('');
                  setSelectedClientIds(new Set());
                }
              }}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteVersionsTarget(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteConfirmText.trim().toLowerCase() !== 'delete'}
              onClick={() => {
                if (deleteVersionsTarget) {
                  onDeleteMultiple(deleteVersionsTarget.groups.flatMap(g => [...g.scenarios.map(s => s.id), ...g.drafts.map(d => d.id)]));
                }
                setDeleteVersionsTarget(null);
                setDeleteConfirmText('');
                setSelectedClientIds(new Set());
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete {deleteVersionsTarget?.groups.length}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Confirmation Dialog */}
      <AlertDialog open={!!deleteAllTarget} onOpenChange={open => { if (!open) setDeleteAllTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all scenarios for {deleteAllTarget?.groupName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {deleteAllTarget?.ids.length} scenario(s). Type
              <span className="font-semibold text-foreground"> delete </span>
              to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Input
              ref={deleteConfirmInputRef}
              placeholder="Type delete to confirm..."
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && deleteConfirmText.trim().toLowerCase() === 'delete' && deleteAllTarget) {
                  e.preventDefault();
                  onDeleteMultiple(deleteAllTarget.ids);
                  setDeleteAllTarget(null);
                  setDeleteConfirmText('');
                }
              }}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteAllTarget(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteConfirmText.trim().toLowerCase() !== 'delete'}
              onClick={() => {
                if (deleteAllTarget) onDeleteMultiple(deleteAllTarget.ids);
                setDeleteAllTarget(null);
                setDeleteConfirmText('');
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer to Org Dialog */}
      <Dialog open={!!transferTarget} onOpenChange={open => { if (!open) setTransferTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer {transferTarget?.groupName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Destination Organization</Label>
              <Select value={transferOrgId} onValueChange={setTransferOrgId}>
                <SelectTrigger><SelectValue placeholder="Select an organization" /></SelectTrigger>
                <SelectContent>
                  {(orgsForTransfer ?? []).map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferTarget(null)}>Cancel</Button>
            <Button onClick={handleTransfer} disabled={!transferOrgId || transferring}>
              {transferring ? 'Transferring…' : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
